import React from "react";
import {
  TextareaAutosize,
  TextareaAutosizeProps,
  Box,
  useTheme,
  Typography,
  FormHelperText,
} from "@mui/material";

interface CoreTextareaProps extends TextareaAutosizeProps {
  /** Sx applied to the textarea when disabled (e.g. { backgroundColor: 'white' }) */
  disabledSx?: Record<string, unknown>;
  maxChars?: number;
}

const CoreTextarea: React.FC<CoreTextareaProps> = ({
  title,
  disabledSx,
  maxChars,
  value,
  ...props
}) => {
  const theme = useTheme();

  const error = maxChars && value && (value as string)?.length > maxChars;
  const helperText = error ? `Maximum ${maxChars} characters allowed.` : '';

  return (
    <Box>
      {title ? (
        <Typography
          variant="body2"
          component="label"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 700,
            mb: 0.5,
            fontSize: "0.875rem",
          }}
        >
          {title}
        </Typography>
      ) : null}

      <Box
        sx={{
          "& textarea": {
            width: "100%",
            fontFamily: theme.typography.fontFamily,
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: "1.25rem",
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            borderRadius: "4px",
            border: "1px solid",
            borderColor: error
              ? theme.palette.error.main
              : theme.palette.divider,
            padding: "12px",
            resize: "vertical",
            transition: "border-color 200ms ease-in-out",
            outline: "none",
            "&::placeholder": {
              color: theme.palette.text.secondary,
              opacity: 1,
            },
            "&:focus": {
              borderColor: error
                ? theme.palette.error.main
                : theme.palette.primary.main,
            },
            "&:hover:not(:focus)": {
              borderColor: error
                ? theme.palette.error.main
                : theme.palette.primary.main,
            },
            "&:disabled": {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              cursor: "not-allowed",
              ...disabledSx,
            },
          },
        }}
      >
        <TextareaAutosize value={value || ''} {...props} />
      </Box>
      {helperText ? (
        <FormHelperText error={!!error} sx={{ mx: 0 }}>
          {helperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
};

export default CoreTextarea;
